import * as R from 'ramda';

import { ReportTypes } from '@atom/types/preferences';
import { isTemplateReportSelectionEnabled } from '@atom/utilities/tenantFeatureUtilities';

export type ReportOption = {
  label: string;
  value: ReportTypes;
  type: string;
  displayEmptyAttributes: boolean;
  requiresAsset: boolean;
};

export const REPORT_OPTIONS: Record<ReportTypes, ReportOption> = {
  [ReportTypes.CUSTOM_V1]: {
    label: 'Assessment Report',
    value: ReportTypes.CUSTOM_V1,
    type: 'custom_v1',
    displayEmptyAttributes: false,
    requiresAsset: true,
  },
  [ReportTypes.CUSTOM_V2]: {
    label: 'Assessment Report',
    value: ReportTypes.CUSTOM_V2,
    type: 'custom_v2',
    displayEmptyAttributes: true,
    requiresAsset: true,
  },
  [ReportTypes.CUSTOM_V3]: {
    label: 'Detailed Report',
    value: ReportTypes.CUSTOM_V3,
    type: 'custom_v3',
    displayEmptyAttributes: true,
    requiresAsset: true,
  },
  [ReportTypes.CUSTOM_WORK_REPORT]: {
    label: 'Default Work Template',
    value: ReportTypes.CUSTOM_WORK_REPORT,
    type: '',
    displayEmptyAttributes: false,
    requiresAsset: false,
  },
  [ReportTypes.HIDOT_REPORT_OVERTIME]: {
    label: 'Planned Overtime Request',
    value: ReportTypes.HIDOT_REPORT_OVERTIME,
    type: '',
    displayEmptyAttributes: false,
    requiresAsset: false,
  },
  [ReportTypes.ALDOT_REPORT]: {
    label: '',
    value: ReportTypes.ALDOT_REPORT,
    type: '',
    displayEmptyAttributes: false,
    requiresAsset: false,
  },
  [ReportTypes.SCDOT_REPORT]: {
    label: '',
    value: ReportTypes.SCDOT_REPORT,
    type: '',
    displayEmptyAttributes: false,
    requiresAsset: false,
  },
  [ReportTypes.BASIC_FULL]: {
    label: 'Full report',
    value: ReportTypes.BASIC_FULL,
    type: 'basic',
    displayEmptyAttributes: true,
    requiresAsset: true,
  },
  [ReportTypes.BASIC_FULL_V2]: {
    label: 'Full report',
    value: ReportTypes.BASIC_FULL_V2,
    type: 'basic',
    displayEmptyAttributes: true,
    requiresAsset: true,
  },
  [ReportTypes.BASIC_PARTIAL]: {
    label: 'Only fields with values',
    value: ReportTypes.BASIC_PARTIAL,
    type: 'basic',
    displayEmptyAttributes: false,
    requiresAsset: true,
  },
};

// This is hard coded mapping of workTemplateId to report type.
// Only HIDOT picks its reports from the work order's template currently.
export const HIDOT_WORK_TEMPLATE_REPORTS: Partial<
  Record<ReportTypes, string[]>
> = {
  [ReportTypes.HIDOT_REPORT_OVERTIME]: ['69fa37c4554e2d2c8e6b8e46'],
};

export const getReportOption = (reportType: string): ReportOption =>
  REPORT_OPTIONS[reportType as ReportTypes];

export const getReportOptions = (
  reportTypes: ReportTypes[] = [],
): ReportOption[] => R.reject(R.isNil, reportTypes.map(getReportOption));

export const getDefaultReportOption = (
  reportTypes: ReportTypes[] = [],
  workTemplateId?: string,
): ReportOption => {
  const options = getReportOptions(reportTypes);

  if (!isTemplateReportSelectionEnabled) {
    return options[0] || null;
  }

  const templateReport = workTemplateId
    ? options.find(option =>
        R.includes(
          workTemplateId,
          HIDOT_WORK_TEMPLATE_REPORTS[option.value] || [],
        ),
      )
    : null;

  const fallback = options.find(
    option => option.value === ReportTypes.CUSTOM_WORK_REPORT,
  );

  return templateReport || fallback || options[0] || null;
};
